import { useEffect, useMemo, useState, useCallback, useRef, lazy, Suspense } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ChevronLeft, Search, Bookmark, BookmarkCheck, Heart,
  BookOpen, MessageCircleQuestion, FlaskConical, Wrench,
  Wheat, ScrollText, ArrowRight
} from "lucide-react";
import { setPendingSave } from "@/lib/pendingSave";
import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { OpenLibraryHero } from "../components/library/OpenLibraryHero";
import { isOpenLibrarySection } from "@/lib/openLibrarySectionCatalog.mjs";

// OPEN_LIBRARY_BUILD1_STRUCTURE

const LibraryAuthorIntroduction = lazy(() => import("@/components/library/LibraryAuthorIntroduction"));
const LibraryDraftAuthorIntroduction = lazy(() => import("@/components/library/LibraryDraftAuthorIntroduction"));

const LIBRARY_SECTIONS = [
  {
    id: "ALL",
    number: "00",
    title: "Все материалы",
    short: "Вся Открытая библиотека",
    description: "Все опубликованные рецепты, статьи и ответы Anokina Studio.",
    Icon: BookOpen,
  },
  {
    id: "RECIPES",
    number: "01",
    title: "Рецепты",
    short: "Готовить",
    description: "Законченные бесплатные рецепты, опубликованные в социальных сетях и специально подготовленные для Anokina Studio.",
    Icon: BookOpen,
  },
  {
    id: "QUESTIONS",
    number: "02",
    title: "Ответы на ваши вопросы",
    short: "Разобраться",
    description: "Понятные ответы на реальные повторяющиеся вопросы аудитории.",
    Icon: MessageCircleQuestion,
  },
  {
    id: "TECHNOLOGY",
    number: "03",
    title: "Технологии",
    short: "Понять",
    description: "Почему это работает: процессы, признаки готовности, хранение и профессиональные объяснения.",
    Icon: FlaskConical,
  },
  {
    id: "TROUBLESHOOTING",
    number: "04",
    title: "Ошибки и решения",
    short: "Исправить",
    description: "Быстрая практическая помощь, когда что-то пошло не так.",
    Icon: Wrench,
  },
  {
    id: "INGREDIENTS",
    number: "05",
    title: "Ингредиенты",
    short: "Узнать",
    description: "Самостоятельные материалы об ингредиентах и их роли в рецептуре.",
    Icon: Wheat,
  },
  {
    id: "HISTORY",
    number: "06",
    title: "Истории и традиции",
    short: "Исследовать",
    description: "История десертов, национальные рецепты и гастрономические традиции.",
    Icon: ScrollText,
  },
];

function inferLibrarySection(material) {
  // Owner choice wins; legacy items without a section use historical inference.
  if (isOpenLibrarySection(material.open_library_section)) return material.open_library_section;
  if (material.primary_master_type === "HISTORICAL_EDITORIAL") return "HISTORY";
  if (material.primary_master_type === "KNOWLEDGE") return "TECHNOLOGY";
  if (material.material_type === "RECIPE" || material.material_type === "FORMULATION") return "RECIPES";
  const hay = `${material.title || ""} ${material.short_description || ""}`.toLowerCase();
  if (/ответ|вопрос|можно ли|чем заменить/.test(hay)) return "QUESTIONS";
  if (/ошиб|не получ|рассло|растека|опада|осел|сырой|сухой|тв[её]рд/.test(hay)) return "TROUBLESHOOTING";
  if (/истор|традиц|происхожд|националь|baci|ricciarelli|cantucci|sabl[eé]|anzac/.test(hay)) return "HISTORY";
  if (/ингредиент|мук|шоколад|сахар|крахмал|орех|миндал|фундук|желатин|пектин/.test(hay)) return "INGREDIENTS";
  return "TECHNOLOGY";
}

const TYPE_LABEL = {
  ARTICLE: "Статьи",
  RECIPE: "Рецепты",
  FORMULATION: "Рецептуры",
  GUIDE: "Гайд",
  REFERENCE: "Справочник",
};

export default function Library({ draftPreview = false }) {
  const nav = useNavigate();
  const { materialId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [items, setItems] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [likedIds, setLikedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const initialSection = LIBRARY_SECTIONS.some((s) => s.id === searchParams.get("section"))
    ? searchParams.get("section")
    : "ALL";
  const [section, setSection] = useState(initialSection);
  const materialsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (submittedQuery.trim()) params.set("search", submittedQuery.trim());
      const [legacyResponse, editorialResponse] = await Promise.all([
        api.get(`/library/materials${params.toString() ? `?${params}` : ""}`),
        api.get("/editorial/public/open-library").catch(() => ({ data: [] })),
      ]);
      const legacy = (legacyResponse.data || []).map((item) => ({ ...item, source_kind: "legacy" }));
      const editorial = Array.isArray(editorialResponse.data) ? editorialResponse.data : [];
      const combined = [...editorial, ...legacy];
      const search = submittedQuery.trim().toLocaleLowerCase("ru");
      setItems(search
        ? combined.filter((item) => `${item.title || ""} ${item.short_description || ""}`.toLowerCase().includes(search))
        : combined);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [submittedQuery]);

  const loadSaved = useCallback(async () => {
    if (!user) { setSavedIds(new Set()); setLikedIds(new Set()); return; }
    try {
      const [s, l] = await Promise.all([api.get("/account/saved"), api.get("/account/likes/ids")]);
      setSavedIds(new Set(s.data.map((m) => m.id)));
      setLikedIds(new Set(l.data));
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleLike = async (material) => {
    if (!user) {
      toast("Войдите, чтобы отметить понравившееся");
      nav("/login");
      return;
    }
    const isLiked = likedIds.has(material.id);
    try {
      if (isLiked) {
        await api.delete(`/account/likes/${material.id}`);
        const next = new Set(likedIds); next.delete(material.id); setLikedIds(next);
      } else {
        await api.post("/account/likes", { material_id: material.id });
        const next = new Set(likedIds); next.add(material.id); setLikedIds(next);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSave = async (material) => {
    if (!user) {
      setPendingSave(material.id);
      toast("Войдите, чтобы сохранить материал", {
        description: "После входа мы автоматически добавим его в «Сохранённое».",
      });
      nav("/login");
      return;
    }
    const isSaved = savedIds.has(material.id);
    try {
      if (isSaved) {
        await api.delete(`/account/saved/${material.id}`);
        const next = new Set(savedIds); next.delete(material.id); setSavedIds(next);
        toast.success("Удалено из «Сохранённое»");
      } else {
        await api.post("/account/saved", { material_id: material.id });
        const next = new Set(savedIds); next.add(material.id); setSavedIds(next);
        toast.success("Сохранено в «Сохранённое»");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const featuredIntro = useMemo(() => items.find((m) =>
    m.source_kind === "editorial" && m.placement === "author_intro"
  ), [items]);
  const catalogItems = useMemo(() => items.filter((m) => m !== featuredIntro), [items, featuredIntro]);

  const sectionCounts = useMemo(() => {
    const counts = Object.fromEntries(LIBRARY_SECTIONS.map((s) => [s.id, 0]));
    counts.ALL = catalogItems.length;
    catalogItems.forEach((m) => { counts[inferLibrarySection(m)] = (counts[inferLibrarySection(m)] || 0) + 1; });
    return counts;
  }, [catalogItems]);

  const visibleItems = useMemo(() => {
    return catalogItems.filter((m) => section === "ALL" || inferLibrarySection(m) === section);
  }, [catalogItems, section]);

  const matchingSections = useMemo(() => {
    const term = submittedQuery.trim().toLocaleLowerCase("ru");
    if (!term) return [];
    return LIBRARY_SECTIONS.filter((s) => s.id !== "ALL" &&
      `${s.title} ${s.short} ${s.description}`.toLocaleLowerCase("ru").includes(term));
  }, [submittedQuery]);

  const chooseSection = (nextSection) => {
    setSection(nextSection);
    const next = new URLSearchParams(searchParams);
    if (nextSection === "ALL") next.delete("section");
    else next.set("section", nextSection);
    setSearchParams(next, { replace: true });
    window.requestAnimationFrame(() => {
      materialsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const activeSection = LIBRARY_SECTIONS.find((s) => s.id === section) || LIBRARY_SECTIONS[0];

  return (
    <div className="min-h-screen bg-cream" data-testid="library-page">
      <div className="max-w-6xl mx-auto px-5">
        <header className="flex flex-col gap-3 pt-5 pb-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="library-top-toolbar">
          <button
            type="button"
            onClick={() => nav(draftPreview ? `/admin/editorial-v2/materials/${encodeURIComponent(materialId)}` : "/hall")}
            data-testid="back-to-hall-button"
            className="inline-flex min-h-11 self-start items-center gap-1.5 rounded-xl border border-[#D7C5AD] bg-white/60 px-3 py-2 text-sm text-espresso transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8A6947]"
            aria-label={draftPreview ? "Вернуться в Редакцию" : "Назад в Anokina Studio"}
          >
            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{draftPreview ? "Вернуться в Редакцию" : "Назад в Anokina Studio"}</span>
          </button>
          <form role="search" onSubmit={(event) => { event.preventDefault(); setSection("ALL"); setSubmittedQuery(q.trim()); }}
            className="flex w-full items-center gap-2 sm:max-w-[380px]" data-testid="library-search-bar">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6947]" aria-hidden="true" />
              <Input data-testid="library-search" value={q} onChange={(event) => setQ(event.target.value)}
                placeholder="Название, тема, раздел" aria-label="Поиск по Открытой библиотеке"
                className="h-12 w-full sm:h-11 rounded-xl border border-[#D7C5AD] bg-white/65 pl-9 pr-3 text-sm text-[#3A281E] placeholder:text-[#887565]" />
            </div>
            {(q || submittedQuery) && <button type="button" onClick={() => { setQ(""); setSubmittedQuery(""); }}
              className="min-h-11 px-2 text-xs text-espresso/70" data-testid="library-search-clear">Очистить</button>}
            <button type="submit" className="min-h-11 shrink-0 rounded-xl bg-[#4A3728] px-4 text-sm text-white"
              data-testid="library-search-submit">Найти</button>
          </form>
        </header>
        <OpenLibraryHero />
      </div>

      <div className="max-w-6xl mx-auto px-5">
        {section === "ALL" && (draftPreview || featuredIntro) && (
          <>
            <a href="#library-sections" className="mt-5 block text-center text-xs text-espresso/60 underline underline-offset-4 hover:text-espresso">
              Перейти к направлениям
            </a>
            <Suspense fallback={<p className="py-6 text-center text-sm">Загрузка…</p>}>
              {draftPreview
                ? <LibraryDraftAuthorIntroduction materialId={materialId} />
                : <LibraryAuthorIntroduction material={featuredIntro} />}
            </Suspense>
          </>
        )}
        {submittedQuery && !loading && (
          <section className="mt-5" aria-live="polite" data-testid="library-search-summary">
            <p className="text-sm text-espresso/75">
              {catalogItems.length + matchingSections.length
                ? `По запросу «${submittedQuery}»: материалов — ${catalogItems.length}, разделов — ${matchingSections.length}`
                : `По запросу «${submittedQuery}» ничего не найдено`}
            </p>
            {matchingSections.length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-testid="library-search-sections">
              {matchingSections.map((item) => (
                <button type="button" key={item.id} onClick={() => { setQ(""); setSubmittedQuery(""); chooseSection(item.id); }}
                  className="min-h-10 rounded-xl border border-[#D7C5AD] bg-white/70 px-3 text-sm text-espresso">
                  Раздел: {item.title}
                </button>
              ))}
            </div>}
          </section>
        )}
        <section id="library-sections" className="mt-7" data-testid="library-sections">
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Выберите направление</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {LIBRARY_SECTIONS.filter((s) => s.id !== "ALL").map((s) => {
              const Icon = s.Icon;
              const active = section === s.id;
              return (
                <button key={s.id} type="button" onClick={() => chooseSection(active ? "ALL" : s.id)}
                  data-testid={`library-section-${s.id}`}
                  className={active
                    ? "min-h-[250px] sm:min-h-[265px] rounded-[22px] border border-espresso/25 bg-[#EADCC8] p-5 text-left transition flex flex-col"
                    : "min-h-[250px] sm:min-h-[265px] rounded-[22px] border border-[#DFCDB4] bg-white/70 p-5 text-left transition hover:bg-white flex flex-col"}>
                  <div className="flex items-start">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F2E7D6] text-espresso">
                      <Icon className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="mt-4 font-serif text-xl text-espresso">{s.title}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.15em] text-espresso/50">{s.short}</div>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground line-clamp-3">{s.description}</p>
                  <div className="mt-auto pt-4 flex items-center justify-between text-xs text-espresso/60">
                    <span>{sectionCounts[s.id] || 0} материалов</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-7 scroll-mt-6" ref={materialsRef}>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-serif text-3xl text-espresso">{activeSection.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{activeSection.description}</p>
            </div>
            {section !== "ALL" && (
              <button onClick={() => chooseSection("ALL")} className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso">
                Показать всё
              </button>
            )}
          </div>
        </section>

        <div className="mt-6 pb-10">
          {loading ? (
            <LoadingGrid />
          ) : visibleItems.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-list">
              {visibleItems.map((m) => (
                <MaterialCard
                  key={m.id}
                  material={m}
                  saved={savedIds.has(m.id)}
                  liked={likedIds.has(m.id)}
                  onToggleSave={() => toggleSave(m)}
                  onToggleLike={() => toggleLike(m)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function MaterialCard({ material, saved, liked, onToggleSave, onToggleLike }) {
  const nav = useNavigate();
  const href = material.source_kind === "editorial"
    ? `/library/editorial/${material.slug || material.id}`
    : `/library/${material.slug || material.id}`;
  const open = () => nav(href);
  return (
    <li
      className="rounded-3xl bg-white border hairline overflow-hidden flex flex-col hover:shadow-md transition-shadow"
      data-testid={`library-card-${material.id}`}
    >
      <button onClick={open} className="text-left" aria-label={`Открыть материал ${material.title}`}>
        <div className="aspect-[4/5] bg-sand/40 relative overflow-hidden">
          {material.cover_image ? (
            <img
              src={resolveMediaUrl(material.cover_image)}
              alt={material.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-3xl text-espresso/40 select-none">
              A
            </div>
          )}
        </div>
      </button>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <span data-testid={`card-type-${material.id}`}>{TYPE_LABEL[material.material_type] || material.material_type}</span>
        </div>
        <button onClick={open} className="text-left">
          <h3 className="font-serif text-lg text-espresso leading-snug hover:text-espressoDark">
            {material.title}
          </h3>
        </button>
        {material.short_description && (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {material.short_description}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <Link
            to={href}
            className="text-xs uppercase tracking-widest text-espresso hover:text-espressoDark"
            data-testid={`card-open-${material.id}`}
          >
            Читать →
          </Link>
          {material.source_kind !== "editorial" && (
            <div className="flex items-center gap-1">
              <button
                onClick={onToggleLike}
                data-testid={`card-like-${material.id}`}
                aria-pressed={liked}
                className={`p-2 rounded-full transition-colors ${liked ? "bg-red-50 text-red-500" : "hover:bg-sand/50 text-espresso"}`}
                title={liked ? "Убрать из понравившихся" : "Нравится"}
              >
                <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} />
              </button>
              <button
                onClick={onToggleSave}
                data-testid={`card-save-${material.id}`}
                aria-pressed={saved}
                className={`p-2 rounded-full transition-colors ${
                  saved ? "bg-espresso text-white" : "hover:bg-sand/50 text-espresso"
                }`}
                title={saved ? "Убрать из сохранённого" : "Сохранить"}
              >
                {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function LoadingGrid() {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-loading">
      {[0, 1, 2].map((i) => (
        <li key={i} className="rounded-3xl bg-white border hairline overflow-hidden">
          <div className="h-40 bg-sand/30 animate-pulse" />
          <div className="p-4 space-y-2">
            <div className="h-3 w-24 bg-sand/40 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-sand/40 rounded animate-pulse" />
            <div className="h-3 w-full bg-sand/40 rounded animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16" data-testid="library-empty">
      <p className="font-serif text-xl text-espresso">Пока пусто</p>
      <p className="text-sm text-muted-foreground mt-2">
        Материалы появятся, как только редакция опубликует их.
      </p>
    </div>
  );
}
